import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Spinner } from '@nextui-org/react';
import {
  ArrowLeft,
  AtSign,
  Calendar,
  Camera,
  Hash,
  ImageOff,
  Lock,
  Music,
  PlayCircle,
  Shield,
  Target,
  User as UserIcon,
  Users,
} from 'lucide-react';
import communityService, { PublicPlayerProfile } from '../services/communityService';

const ACCENT = '#3fced1';

interface SocialLink {
  label: string;
  url: string;
  icon: React.ReactNode;
  color: string;
}

function buildSocialLinks(profile: PublicPlayerProfile | null): SocialLink[] {
  const sm = profile?.socialMedia;
  if (!sm) return [];
  const links: SocialLink[] = [];
  const clean = (v: string) => v.replace('@', '');
  if (sm.instagram) {
    links.push({
      label: 'Instagram',
      url: sm.instagram.startsWith('http')
        ? sm.instagram
        : `https://instagram.com/${clean(sm.instagram)}`,
      icon: <Camera className="w-4 h-4" />,
      color: '#E1306C',
    });
  }
  if (sm.facebook) {
    links.push({
      label: 'Facebook',
      url: sm.facebook.startsWith('http') ? sm.facebook : `https://facebook.com/${sm.facebook}`,
      icon: <Hash className="w-4 h-4" />,
      color: '#1877F2',
    });
  }
  if (sm.twitter) {
    links.push({
      label: 'X / Twitter',
      url: sm.twitter.startsWith('http') ? sm.twitter : `https://twitter.com/${clean(sm.twitter)}`,
      icon: <AtSign className="w-4 h-4" />,
      color: '#1DA1F2',
    });
  }
  if (sm.tiktok) {
    links.push({
      label: 'TikTok',
      url: sm.tiktok.startsWith('http') ? sm.tiktok : `https://tiktok.com/@${clean(sm.tiktok)}`,
      icon: <Music className="w-4 h-4" />,
      color: '#a8a8a8',
    });
  }
  if (sm.youtube) {
    links.push({
      label: 'YouTube',
      url: sm.youtube.startsWith('http') ? sm.youtube : `https://youtube.com/@${clean(sm.youtube)}`,
      icon: <PlayCircle className="w-4 h-4" />,
      color: '#FF0000',
    });
  }
  return links;
}

/**
 * Öffentliches Community-Spielerprofil (Port der Flutter player_profile_view).
 * Lädt /api/player/:username — funktioniert sowohl mit :username als auch :slug als Routen-Parameter.
 */
const PublicProfilePage: React.FC = () => {
  const params = useParams<{ username?: string; slug?: string }>();
  const username = params.username ?? params.slug ?? '';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<PublicPlayerProfile | null>(null);

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    const result = await communityService.getPublicProfile(username);
    setProfile(result);
    setLoading(false);
  }, [username]);

  useEffect(() => {
    load();
    window.scrollTo({ top: 0 });
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <UserIcon className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Profil nicht gefunden</h2>
          <p className="text-zinc-400 mb-6 text-sm">
            Das gesuchte Spielerprofil existiert nicht oder ist nicht verfügbar.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
        </div>
      </div>
    );
  }

  if (profile.isPrivate) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Helmet>
          <title>{profile.username} | DartCloud</title>
        </Helmet>
        <div className="text-center max-w-md">
          <Lock className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Dieses Profil ist privat</h2>
          <p className="text-zinc-400 mb-6 text-sm">
            {profile.username} hat sein Profil nicht öffentlich freigegeben.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
        </div>
      </div>
    );
  }

  const headerUrl = communityService.buildImageUrl(profile.headerImage);
  const avatarUrl = communityService.buildImageUrl(profile.image);
  const memberSince = communityService.formatMemberSince(profile.memberSince);
  const socialLinks = buildSocialLinks(profile);
  const dartSetups = profile.dartSetups ?? [];
  const clubMemberships = profile.clubMemberships ?? [];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Helmet>
        <title>{profile.username} - Spielerprofil | DartCloud</title>
        <meta
          name="description"
          content={
            profile.bio
              ? `${profile.username} auf DartCloud: ${profile.bio.substring(0, 150)}`
              : `Öffentliches Spielerprofil von ${profile.username} in der DartCloud Community.`
          }
        />
      </Helmet>

      {/* Header-Bild */}
      <div className="relative w-full h-48 sm:h-64">
        {headerUrl ? (
          <>
            <img
              src={headerUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-zinc-950" />
          </>
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(135deg, ${ACCENT}66 0%, #0078B466 100%)` }}
          />
        )}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 p-2 rounded-full bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
          aria-label="Zurück"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Avatar + Name */}
        <div className="relative -mt-14 sm:-mt-16 flex flex-col items-center text-center">
          <div className="rounded-full p-1 bg-zinc-950">
            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center border-2 border-zinc-800">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={profile.username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <UserIcon size={48} className="text-zinc-600" />
              )}
            </div>
          </div>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold">{profile.username}</h1>
          {memberSince && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-zinc-400">
              <Calendar className="w-4 h-4" />
              Mitglied seit {memberSince}
            </p>
          )}

          {/* Social Media */}
          {socialLinks.length > 0 && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {socialLinks.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors hover:opacity-80"
                  style={{
                    borderColor: `${link.color}55`,
                    backgroundColor: `${link.color}15`,
                    color: link.color,
                  }}
                >
                  {link.icon}
                  <span>{link.label}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Bio */}
        {profile.bio && (
          <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-900/70 p-6">
            <div className="flex items-center gap-2 mb-3">
              <UserIcon className="w-5 h-5" style={{ color: ACCENT }} />
              <h2 className="text-lg font-semibold">Über mich</h2>
            </div>
            <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
          </div>
        )}

        {/* Vereine */}
        {clubMemberships.length > 0 && (
          <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/70 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5" style={{ color: ACCENT }} />
              <h2 className="text-lg font-semibold">Vereine</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {clubMemberships.map((m, i) => {
                const clubImageUrl = communityService.buildThumbnailUrl(m.clubImage, 100);
                const memberSinceLabel = communityService.formatMemberSince(m.memberSince);
                const inner = (
                  <>
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
                      {clubImageUrl ? (
                        <img
                          src={clubImageUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Shield className="w-5 h-5 text-zinc-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {m.clubName || 'Verein'}
                      </p>
                      {memberSinceLabel && (
                        <p className="text-xs text-zinc-500">Mitglied seit {memberSinceLabel}</p>
                      )}
                    </div>
                  </>
                );
                return m.clubSlug ? (
                  <RouterLink
                    key={m.id || i}
                    to={`/verein/${encodeURIComponent(m.clubSlug)}`}
                    className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950/60 hover:border-zinc-700 hover:bg-zinc-900 p-3 transition-colors"
                  >
                    {inner}
                  </RouterLink>
                ) : (
                  <div
                    key={m.id || i}
                    className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3"
                  >
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dart-Setups */}
        <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/70 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5" style={{ color: ACCENT }} />
            <h2 className="text-lg font-semibold">
              Dart-Setups {dartSetups.length > 0 && `(${dartSetups.length})`}
            </h2>
          </div>
          {dartSetups.length === 0 ? (
            <p className="text-sm text-zinc-500">
              {profile.username} hat noch keine Dart-Setups veröffentlicht.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {dartSetups.map((setup) => {
                const imgUrl = communityService.buildThumbnailUrl(setup.image, 400);
                return (
                  <RouterLink
                    key={setup.id}
                    to={`/setup/${encodeURIComponent(setup.id)}`}
                    className="rounded-xl border border-zinc-800 bg-zinc-950/60 overflow-hidden hover:border-zinc-700 transition-colors group"
                  >
                    <div className="aspect-square bg-zinc-900 flex items-center justify-center overflow-hidden">
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={setup.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <ImageOff size={32} className="text-zinc-600" />
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-sm font-semibold text-white truncate">{setup.title}</p>
                      {setup.subtitle && (
                        <p className="text-xs text-zinc-500 truncate mt-0.5">{setup.subtitle}</p>
                      )}
                    </div>
                  </RouterLink>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicProfilePage;
